import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle, Download, FileText, Loader2, Printer, ScrollText, ShieldAlert, Trash2,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { todayISO } from '../lib/dates'
import { buildGuestExport, guestLabel, type GuestProfile } from '../lib/guests'
import { Badge, Button, Card, cx } from '../ui/kit'
import { downloadJson, slugify } from './guestUi'
import { darf, werDarf } from '../lib/rollen'
import { useAuth } from '../auth'

/**
 * Der DSGVO-Teil der Kartei: Auskunft, Anonymisierung, Protokoll.
 *
 * HIER LIEGT EIN ECHTER WIDERSPRUCH, und die Oberfläche muss ihn zeigen statt ihn
 * zu verschweigen. Art. 17 DSGVO gibt dem Gast ein Recht auf Löschung. § 132 BAO
 * (Österreich) und § 147 AO (Deutschland) verlangen, Rechnungen sieben bzw. acht
 * Jahre aufzubewahren, und auf einer Rechnung steht der Name des Empfängers.
 *
 * Die Auflösung ist keine Löschung, sondern eine Anonymisierung: alles, was nicht
 * aufbewahrungspflichtig ist, verschwindet wirklich; die Rechnungen bleiben
 * unangetastet; die Buchungshistorie bleibt ohne Personenbezug bestehen. Was das im
 * Einzelfall heißt, rechnet `guest_erasure_preview()` aus — und der Vermieter sieht
 * es, BEVOR er den Knopf drückt. Ein „Wirklich löschen?" ohne Aufstellung ist bei
 * einem unumkehrbaren Vorgang keine Rückfrage, sondern eine Formalität.
 *
 * Keine Rechtsberatung: was hier steht, beschreibt, was die Software tut. Ob im
 * konkreten Fall länger aufzubewahren ist — laufende Betriebsprüfung, Rechtsstreit,
 * abweichende Landesregeln —, muss der Betrieb selbst klären.
 */

interface ErasurePreview {
  guest_id: string
  retention_years: number
  registration_retention_months: number
  removes: {
    contact: boolean
    has_special_category: boolean
    has_notes: boolean
    tags: number
    booking_notes: number
    mails: number
    enquiries: number
    registration_people: number
  }
  keeps: {
    issued_invoices: number
    invoice_numbers: string[]
    last_invoice_date: string | null
    keep_until: string | null
    bookings: number
    registration_people_locked: number
    registration_locked_until: string | null
  }
  blockers: {
    future_bookings: number
    open_invoices: number
    deposits_held: number
  }
}

interface LogRow {
  id: number
  action: 'anonymize' | 'export' | 'merge'
  actor_email: string | null
  created_at: string
  detail: Record<string, unknown>
}

export default function GuestPrivacyPanel({
  guest, onChanged,
}: { guest: GuestProfile; onChanged: () => void }) {
  const { rolle } = useAuth()
  const darfGastdaten = darf(rolle, 'gastdaten')
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const live = store.live && Boolean(supabase)

  const [preview, setPreview] = useState<ErasurePreview | null>(null)
  const [log, setLog] = useState<LogRow[]>([])
  const [busy, setBusy] = useState<'preview' | 'export' | 'anonymize' | 'delete' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const loadLog = useCallback(async () => {
    if (!live) return
    const { data } = await supabase!.from('guest_privacy_log')
      .select('id,action,actor_email,created_at,detail')
      .eq('guest_id', guest.id).order('created_at', { ascending: false })
    setLog((data ?? []) as LogRow[])
  }, [live, guest.id])

  useEffect(() => { void loadLog() }, [loadLog])

  const loadPreview = async () => {
    if (!live) return
    setBusy('preview'); setError(null)
    const { data, error: e } = await supabase!.rpc('guest_erasure_preview', { p_guest: guest.id })
    if (e) setError(e.message)
    else setPreview(data as ErasurePreview)
    setBusy(null)
  }

  /**
   * Auskunft nach Art. 15 und Datenübertragbarkeit nach Art. 20.
   *
   * Die Datei wird im Browser zusammengesetzt — Kartei, Buchungen, Rechnungen und
   * Zahlungen liegen dort ohnehin vollständig vor. Serverseitig kommen nur die
   * Meldescheinzeilen und das Löschprotokoll dazu, und im Demomodus fehlen sie eben.
   */
  const buildDocument = async () => {
    const doc = buildGuestExport({
      guest,
      guestId: guest.id,
      bookings: store.db.bookings,
      invoices: store.db.invoices,
      payments: store.db.payments,
      today: todayISO(),
      tenantName: store.db.tenant.name,
      unitName: (id) => store.unitById(id)?.name ?? id,
    })
    if (live) {
      const { data, error: e } = await supabase!.rpc('guest_data_export', { p_guest: guest.id })
      if (e) throw new Error(e.message)
      const extra = data as { registration_people?: unknown[]; privacy_log?: unknown[] }
      doc.meldescheine = extra.registration_people ?? []
      doc.loeschprotokoll = extra.privacy_log ?? []
    }
    return doc
  }

  const exportJson = async () => {
    setBusy('export'); setError(null); setNote(null)
    try {
      const doc = await buildDocument()
      downloadJson(`auskunft-${slugify(guestLabel(guest))}-${todayISO()}.json`, doc)
      // Der Protokolleintrag entsteht in `guest_data_export()`. In der Demo gibt es
      // die Funktion nicht — dann darf hier auch nicht behauptet werden, es sei
      // etwas vermerkt worden.
      setNote(de
        ? `Auskunft erstellt.${live ? ' Die Erteilung wurde im Protokoll vermerkt.' : ''}`
        : `Report created.${live ? ' The disclosure was recorded in the log.' : ''}`)
      await loadLog()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    setBusy(null)
  }

  /**
   * Dieselbe Auskunft als druckbare Seite.
   *
   * Als Datei zum Herunterladen und nicht als neues Fenster: ein `window.open` beim
   * Klick auf „Drucken" landet auf jedem zweiten Rechner im Popup-Blocker, und der
   * Vermieter sieht dann gar nichts. Eine HTML-Datei öffnet er selbst und druckt sie
   * über den Browser ins PDF, das er dem Gast schickt.
   */
  const exportPrintable = async () => {
    setBusy('export'); setError(null); setNote(null)
    try {
      const doc = await buildDocument()
      const html = renderPrintable(doc, de)
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `auskunft-${slugify(guestLabel(guest))}-${todayISO()}.html`
      a.click()
      URL.revokeObjectURL(url)
      setNote(de
        ? 'Druckfassung erstellt — im Browser öffnen und als PDF drucken.'
        : 'Printable version created — open it and print to PDF.')
      await loadLog()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    setBusy(null)
  }

  /*
   * Endgültig löschen — nur für einen Gast, an dem nichts hängt.
   *
   * Anonymisieren ist für einen Gast MIT Geschichte richtig: Buchungen bleiben,
   * Rechnungen bleiben lesbar. Für den, der vor fünf Minuten versehentlich
   * doppelt angelegt wurde, ist es falsch — er stünde auf ewig als
   * „Anonymisiert" in der Kartei.
   *
   * Die Prüfung, ob wirklich nichts daran hängt, macht die Datenbank
   * (`delete_guest`, Migration 0056) und nicht dieser Knopf: alle
   * Fremdschlüssel auf `guests` stehen auf ON DELETE SET NULL, ein ungeprüftes
   * Löschen nähme also einer Rechnung stillschweigend den Empfänger.
   */
  const loeschen = async () => {
    const frage = de
      ? `${guestLabel(guest)} endgültig löschen?\n\nDer Gast verschwindet vollständig — kein Eintrag, keine Spur in der Kartei. Das lässt sich nicht rückgängig machen.`
      : `Delete ${guestLabel(guest)} permanently?\n\nThe guest disappears entirely — no entry, no trace. This cannot be undone.`
    if (!window.confirm(frage)) return

    setBusy('delete'); setError(null); setNote(null)
    const { error: e } = await supabase!.rpc('delete_guest', { p_guest: guest.id })
    setBusy(null)
    if (e) { setError(e.message); return }
    store.reload()
    onChanged()
  }

  const anonymize = async () => {
    if (!preview) return
    const r = preview.removes
    const k = preview.keeps
    const summary = de
      ? `${guestLabel(guest)} anonymisieren?\n\n`
        + 'GELÖSCHT WIRD:\n'
        + '• Kontaktdaten, Anschrift, Geburtsdatum, Ausweis\n'
        + '• Vorlieben, Schlagworte, Notizen\n'
        + (r.has_special_category ? '• Allergien und Ernährung (Art. 9 DSGVO)\n' : '')
        + (r.booking_notes > 0 ? `• Notizen an ${r.booking_notes} Buchung(en)\n` : '')
        + (r.mails > 0 ? `• Inhalt von ${r.mails} versendeten Mail(s)\n` : '')
        + (r.registration_people > 0 ? `• ${r.registration_people} Meldescheinzeile(n)\n` : '')
        + '\nBLEIBT BESTEHEN:\n'
        + `• ${k.issued_invoices} ausgestellte Rechnung(en)`
        + (k.keep_until ? ` — aufzubewahren bis ${formatDate(k.keep_until)}` : '') + '\n'
        + `• ${k.bookings} Buchung(en), künftig ohne Namen\n`
        + (k.registration_people_locked > 0
          ? `• ${k.registration_people_locked} Meldescheinzeile(n), Frist läuft noch\n` : '')
        + '\nDer Vorgang lässt sich nicht rückgängig machen.'
      : `Anonymise ${guestLabel(guest)}?\n\n`
        + 'WILL BE DELETED: contact details, address, preferences, notes'
        + (r.has_special_category ? ', allergies and dietary data (Art. 9 GDPR)' : '') + '.\n'
        + `WILL REMAIN: ${k.issued_invoices} issued invoice(s)`
        + (k.keep_until ? ` — to be kept until ${formatDate(k.keep_until)}` : '')
        + `, ${k.bookings} booking(s) without a name.\n\nThis cannot be undone.`
    if (!window.confirm(summary)) return

    setBusy('anonymize'); setError(null); setNote(null)
    const { error: e } = await supabase!.rpc('anonymize_guest', { p_guest: guest.id })
    if (e) setError(e.message)
    else {
      setNote(de ? 'Gast anonymisiert.' : 'Guest anonymised.')
      setPreview(null)
      onChanged()
      store.reload()
      await loadLog()
    }
    setBusy(null)
  }

  const blocked = preview
    ? preview.blockers.future_bookings > 0 || preview.blockers.open_invoices > 0
      || Number(preview.blockers.deposits_held) > 0
    : false

  return (
    <Card className="p-4 space-y-3">
      <h3 className="font-medium flex items-center gap-2">
        <ShieldAlert size={16} /> {de ? 'Datenschutz' : 'Data protection'}
      </h3>

      {guest.anonymized_at && (
        <p className="text-sm rounded-xl bg-surface-2 p-3">
          {de
            ? `Dieser Datensatz wurde am ${formatDate(guest.anonymized_at.slice(0, 10))} anonymisiert. Die Buchungshistorie besteht ohne Personenbezug weiter, die ausgestellten Rechnungen bleiben unverändert.`
            : `This record was anonymised on ${formatDate(guest.anonymized_at.slice(0, 10))}. The booking history remains without a personal reference; issued invoices are untouched.`}
        </p>
      )}

      {/* Auskunft — funktioniert auch in der Demo, weil sie im Browser entsteht. */}
      <div className="space-y-2">
        <p className="text-sm text-muted">
          {de
            ? 'Auskunft nach Art. 15 und Datenübertragbarkeit nach Art. 20 DSGVO. Eine Anfrage ist binnen eines Monats zu beantworten.'
            : 'Access under Art. 15 and portability under Art. 20 GDPR. A request must be answered within one month.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void exportJson()} disabled={busy !== null || !darfGastdaten}>
            {busy === 'export' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {de ? 'Auskunft als Datei' : 'Report as file'}
          </Button>
          <Button size="sm" onClick={() => void exportPrintable()} disabled={busy !== null || !darfGastdaten}>
            <Printer size={14} />
            {de ? 'Druckfassung' : 'Printable'}
          </Button>
        </div>
      </div>

      <hr className="border-border" />

      {/* Anonymisierung — braucht den Server. */}
      {!live ? (
        <p className="text-sm text-muted rounded-xl bg-surface-2 p-3">
          {de
            ? 'Anonymisieren, Zusammenführen und das Löschprotokoll laufen in der Datenbank und stehen deshalb nur im echten Konto zur Verfügung. Die Auskunft oben funktioniert auch hier.'
            : 'Anonymising, merging and the deletion log run in the database and are therefore only available in a real account. The report above works here too.'}
        </p>
      ) : !guest.anonymized_at && (
        <div className="space-y-3">
          <p className="text-sm text-muted">
            {de
              ? 'Löschung nach Art. 17 DSGVO. Rechnungen sind gesetzlich aufzubewahren und können nicht gelöscht werden — alles andere schon.'
              : 'Erasure under Art. 17 GDPR. Invoices must be kept by law and cannot be deleted — everything else can.'}
          </p>

          {!preview ? (
            <Button size="sm" onClick={() => void loadPreview()} disabled={busy !== null || !darfGastdaten}>
              {busy === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {de ? 'Prüfen, was gelöscht würde' : 'Check what would be deleted'}
            </Button>
          ) : (
            <div className="space-y-3">
              <PreviewBlock preview={preview} de={de} formatDate={formatDate} formatMoney={formatMoney} />
              {blocked ? (
                <p className="text-sm text-warn flex gap-2">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  {de
                    ? 'Solange ein Aufenthalt bevorsteht, eine Rechnung offen ist oder eine Kaution gehalten wird, sind die Daten zur Vertragserfüllung erforderlich (Art. 17 Abs. 3 lit. b DSGVO). Erst abschließen, dann anonymisieren.'
                    : 'While a stay is upcoming, an invoice is open or a deposit is held, the data is needed to perform the contract (Art. 17(3)(b) GDPR). Close those first.'}
                </p>
              ) : (
                <div className="space-y-2">
                  <Button
                    size="sm" variant="danger"
                    onClick={() => void anonymize()} disabled={busy !== null || !darfGastdaten}
                  >
                    {busy === 'anonymize'
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />}
                    {de ? 'Jetzt anonymisieren' : 'Anonymise now'}
                  </Button>

                  {/* Der zweite Weg, für den versehentlich angelegten Gast.
                      Er scheitert mit einer erklärenden Meldung, sobald etwas
                      daran hängt — die Datenbank entscheidet das, nicht dieser
                      Knopf. */}
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => void loeschen()} disabled={busy !== null || !darfGastdaten}
                  >
                    {busy === 'delete'
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} />}
                    {de ? 'Endgültig löschen (nur ohne Buchungen)'
                        : 'Delete permanently (only without bookings)'}
                  </Button>
                  <p className="text-xs text-muted">
                    {de
                      ? 'Löschen geht nur, solange keine Buchung, Rechnung oder Nachricht auf den Gast zeigt. Sonst bleibt Anonymisieren — dort bleibt die Geschichte erhalten.'
                      : 'Deleting only works while no booking, invoice or message points to this guest. Otherwise anonymise — that keeps the history.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-danger">{error}</p>}
      {note && <p className="text-sm text-ok">{note}</p>}

      {/* Protokoll — Nachweis nach Art. 5 Abs. 2 DSGVO. */}
      {live && log.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-medium text-muted flex items-center gap-1.5">
            <ScrollText size={13} /> {de ? 'Protokoll' : 'Log'}
          </h4>
          <ul className="text-xs text-muted space-y-1">
            {log.map((row) => (
              <li key={row.id} className="flex flex-wrap gap-x-2">
                <span className="tabular-nums">
                  {new Date(row.created_at).toLocaleString(de ? 'de-AT' : 'en-GB')}
                </span>
                <span className="font-medium text-fg">
                  {row.action === 'anonymize' ? (de ? 'anonymisiert' : 'anonymised')
                    : row.action === 'export' ? (de ? 'Auskunft erteilt' : 'report issued')
                    : (de ? 'zusammengeführt' : 'merged')}
                </span>
                {row.actor_email && <span>· {row.actor_email}</span>}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted">
            {de
              ? 'Das Protokoll hält fest, WAS geschehen ist — nicht, welche Daten gelöscht wurden. Ein Protokoll, das den Inhalt aufbewahrt, wäre keine Löschung.'
              : 'The log records WHAT happened — never the deleted content itself.'}
          </p>
        </div>
      )}
    {!darfGastdaten && (
        <p className="text-xs text-muted pt-2">{werDarf('gastdaten')}</p>
      )}
    </Card>
  )
}

function PreviewBlock({
  preview, de, formatDate, formatMoney,
}: {
  preview: ErasurePreview
  de: boolean
  formatDate: (iso: string) => string
  formatMoney: (n: number) => string
}) {
  const r = preview.removes
  const k = preview.keeps
  const b = preview.blockers
  const removed: string[] = []
  if (r.contact) removed.push(de ? 'Kontaktdaten und Anschrift' : 'contact details and address')
  removed.push(de ? 'Vorlieben, Fahrzeug, Haustier, Anlass' : 'preferences, vehicle, pet, occasion')
  if (r.has_notes) removed.push(de ? 'interne Notiz' : 'internal note')
  if (r.tags > 0) removed.push(de ? `${r.tags} Schlagwort(e)` : `${r.tags} tag(s)`)
  if (r.booking_notes > 0) {
    removed.push(de ? `Notizen an ${r.booking_notes} Buchung(en)` : `notes on ${r.booking_notes} booking(s)`)
  }
  if (r.mails > 0) removed.push(de ? `Inhalt von ${r.mails} Mail(s)` : `content of ${r.mails} mail(s)`)
  if (r.enquiries > 0) removed.push(de ? `${r.enquiries} Anfrage(n)` : `${r.enquiries} enquiry/enquiries`)
  if (r.registration_people > 0) {
    removed.push(de
      ? `${r.registration_people} Meldescheinzeile(n) mit abgelaufener Frist`
      : `${r.registration_people} registration row(s) past their retention period`)
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-xl bg-danger-soft/60 p-3 space-y-1">
        <div className="font-medium text-danger flex items-center gap-1.5">
          <Trash2 size={14} /> {de ? 'Wird gelöscht' : 'Will be deleted'}
        </div>
        <ul className="list-disc pl-5 text-muted space-y-0.5">
          {removed.map((x) => <li key={x}>{x}</li>)}
        </ul>
        {r.has_special_category && (
          <p className="text-danger font-medium pt-1">
            {de
              ? 'Darunter Allergien und Ernährung — besondere Kategorie nach Art. 9 DSGVO.'
              : 'Including allergies and dietary data — special category under Art. 9 GDPR.'}
          </p>
        )}
      </div>

      <div className="rounded-xl bg-surface-2 p-3 space-y-1">
        <div className="font-medium flex items-center gap-1.5">
          <FileText size={14} /> {de ? 'Bleibt bestehen — und warum' : 'Will remain — and why'}
        </div>
        <ul className="list-disc pl-5 text-muted space-y-0.5">
          <li>
            {de
              ? `${k.issued_invoices} ausgestellte Rechnung(en)`
              : `${k.issued_invoices} issued invoice(s)`}
            {k.keep_until && (de
              ? ` — aufzubewahren bis ${formatDate(k.keep_until)} (${preview.retention_years} Jahre ab Jahresende, § 132 BAO / § 147 AO)`
              : ` — to be kept until ${formatDate(k.keep_until)} (${preview.retention_years} years from year end)`)}
            {k.invoice_numbers.length > 0 && (
              <span className="block text-xs opacity-80">{k.invoice_numbers.join(', ')}</span>
            )}
          </li>
          <li>
            {de
              ? `${k.bookings} Buchung(en) — Zeitraum, Einheit und Preis bleiben, der Name fällt weg`
              : `${k.bookings} booking(s) — dates, unit and price stay, the name goes`}
          </li>
          {k.registration_people_locked > 0 && (
            <li>
              {de
                ? `${k.registration_people_locked} Meldescheinzeile(n) — Aufbewahrungsfrist läuft noch`
                : `${k.registration_people_locked} registration row(s) — retention period still running`}
              {k.registration_locked_until && ` (${formatDate(k.registration_locked_until)})`}
            </li>
          )}
        </ul>
      </div>

      {(b.future_bookings > 0 || b.open_invoices > 0 || Number(b.deposits_held) > 0) && (
        <div className="rounded-xl bg-warn-soft p-3 space-y-1">
          <div className="font-medium text-warn flex items-center gap-1.5">
            <AlertTriangle size={14} /> {de ? 'Hält die Löschung auf' : 'Blocking the erasure'}
          </div>
          <ul className="list-disc pl-5 text-muted space-y-0.5">
            {b.future_bookings > 0 && (
              <li>{de ? `${b.future_bookings} laufende oder künftige Buchung(en)` : `${b.future_bookings} current or future booking(s)`}</li>
            )}
            {b.open_invoices > 0 && (
              <li>{de ? `${b.open_invoices} offene Rechnung(en)` : `${b.open_invoices} open invoice(s)`}</li>
            )}
            {Number(b.deposits_held) > 0 && (
              <li>{de ? `Kaution von ${formatMoney(Number(b.deposits_held))} wird gehalten` : `deposit of ${formatMoney(Number(b.deposits_held))} held`}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Die Auskunft als Seite, die man ausdrucken und dem Gast schicken kann.
 *
 * Bewusst kein Nachbau der Belegvorlage: eine Auskunft ist kein Geschäftsbrief,
 * sondern eine Aufstellung, und sie muss vor allem VOLLSTÄNDIG und lesbar sein.
 */
function renderPrintable(doc: ReturnType<typeof buildGuestExport>, de: boolean): string {
  const esc = (v: unknown) => String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const rows = (obj: Record<string, unknown>): string =>
    Object.entries(obj)
      .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
      .map(([key, value]) => `<tr><th>${esc(key.replace(/_/g, ' '))}</th><td>${
        typeof value === 'object' && value !== null
          ? `<table>${rows(value as Record<string, unknown>)}</table>`
          : esc(value)
      }</td></tr>`)
      .join('')
  const table = (title: string, list: Record<string, unknown>[]): string => {
    if (list.length === 0) return ''
    const keys = Object.keys(list[0]!)
    return `<h2>${esc(title)}</h2><table class="grid"><tr>${
      keys.map((k) => `<th>${esc(k.replace(/_/g, ' '))}</th>`).join('')
    }</tr>${
      list.map((row) => `<tr>${keys.map((k) => `<td>${esc(row[k])}</td>`).join('')}</tr>`).join('')
    }</table>`
  }

  const a9 = doc.besondere_kategorien_art9
  return `<!doctype html><html lang="${de ? 'de' : 'en'}"><meta charset="utf-8">
<title>${de ? 'Auskunft nach Art. 15 DSGVO' : 'Access report under Art. 15 GDPR'}</title>
<style>
 body{font:13px/1.5 system-ui,sans-serif;max-width:52rem;margin:2rem auto;padding:0 1rem;color:#111}
 h1{font-size:1.4rem;margin-bottom:.2rem} h2{font-size:1rem;margin:1.6rem 0 .4rem;border-bottom:1px solid #ddd;padding-bottom:.2rem}
 table{border-collapse:collapse;width:100%} th{text-align:left;font-weight:600;vertical-align:top;padding:.2rem .6rem .2rem 0;width:14rem}
 td{padding:.2rem 0;vertical-align:top}
 table.grid th,table.grid td{border-bottom:1px solid #eee;padding:.3rem .5rem;width:auto;font-size:12px}
 .note{background:#f6f7f9;padding:.8rem 1rem;border-radius:.5rem;margin:.8rem 0}
 .art9{background:#fff4f4;border:1px solid #f0caca;padding:.8rem 1rem;border-radius:.5rem;margin:.8rem 0}
 @media print{body{margin:0}}
</style>
<h1>${de ? 'Auskunft über gespeicherte Daten' : 'Report on stored personal data'}</h1>
<p>${esc(doc.auskunft.betrieb)} · ${new Date(doc.auskunft.erstellt_am).toLocaleString(de ? 'de-AT' : 'en-GB')}</p>
<div class="note">${esc(doc.auskunft.rechtsgrundlage)}<br>${esc(doc.auskunft.hinweis)}</div>
<h2>${de ? 'Stammdaten' : 'Master data'}</h2><table>${rows(doc.stammdaten)}</table>
<div class="art9"><strong>${de
    ? 'Besondere Kategorien personenbezogener Daten (Art. 9 DSGVO)'
    : 'Special categories of personal data (Art. 9 GDPR)'}</strong><br>${
  a9.vorhanden
    ? `<table>${rows({
      [de ? 'Allergien' : 'Allergies']: a9.allergien,
      [de ? 'Ernährung' : 'Dietary']: a9.ernaehrung,
      [de ? 'Rechtsgrundlage' : 'Legal basis']: a9.rechtsgrundlage,
      [de ? 'Erfasst am' : 'Recorded at']: a9.erfasst_am,
    })}</table>`
    : (de ? 'Es sind keine Daten dieser Art gespeichert.' : 'No data of this kind is stored.')
}</div>
<h2>${de ? 'Einwilligungen' : 'Consents'}</h2><table>${rows(doc.einwilligungen)}</table>
<h2>${de ? 'Errechnete Kennzahlen' : 'Derived figures'}</h2><table>${
  rows(doc.kennzahlen as unknown as Record<string, unknown>)}</table>
${table(de ? 'Aufenthalte' : 'Stays', doc.aufenthalte)}
${table(de ? 'Rechnungen' : 'Invoices', doc.rechnungen)}
${table(de ? 'Zahlungen' : 'Payments', doc.zahlungen)}
${table(de ? 'Meldescheine' : 'Registration forms',
    (doc.meldescheine ?? []) as Record<string, unknown>[])}
</html>`
}

/** Kleiner Helfer für die Liste: Kennzeichnung eines anonymisierten Datensatzes. */
export function AnonymizedBadge({ de }: { de: boolean }) {
  return (
    <Badge tone="neutral" className={cx('shrink-0')}>
      {de ? 'anonymisiert' : 'anonymised'}
    </Badge>
  )
}
